// Copyright (c) 2016 Deere & Company

import React, {PropTypes} from 'react';
import {getFieldClassNameForClaimFied, isValidClaim, isValidationRequiredForClaimFied} from '../../services/claim-validation-helper';
import {
    getRepairedDateErrorMessage,
    getVsrMessage,
    hasVsrErrorForClaimField,
    isRepairDateAfterFailureDate,
    isResponsibleAccountEditable
} from '../../../shared/services/claim-shared-helper';
import AmountOfUse from '../../../shared/components/claim-fields/amount-of-use/amount-of-use-without-compliance';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import ClaimRgaStatusIndicator from '../../../shared/components/claim-rga-status-indicator';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FormValidation from '../../state/form-validation';
import {FormattedMessage} from 'react-intl';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import LabeledDateInput from '../../../shared/components/claim-fields/labeled-date-input';
import LabeledInput from '../../../shared/components/claim-fields/labeled-input';
import LabeledInputWithBlur from '../../../shared/components/claim-fields/labeled-input-with-blur';
import LabeledKeyValueDropDown from '../../../shared/components/claim-fields/labeled-key-value-dropdown';
import Product from '@deere/cps-it.warranty-model-factory/lib/product';
import ResponsibleAccounts from '../../../shared/components/claim-fields/responsible-accounts';
import {SERVICE_PART_FAILURE} from '../../constants';
import SpaClaim from '@deere/cps-it.warranty-model-factory/lib/claim-special-allowance';
import {fixCursorPosition} from '../../../../shared/services/fix-cursor-position';
import {formatPin} from '../../../../shared/services/format-pin';
import {isValidDate} from '../../../../shared/services/build-date';
import moment from 'moment';

function handleInputBlur(property, value) {

    this.actions.updateSpaClaimInputOnBlur(property, value, this.claim);

}

function resetCustomerProductDetails() {

    this.actions.resetCustomerDetails();
    this.actions.resetCkcCustomerFailure();
    this.actions.resetSpaProductDetails();

}

function handlePinBlur(pin) {

    this::handleInputBlur('pin', pin);

    if (pin) {

        if (pin !== this.product.pin) {

            this::resetCustomerProductDetails();
            this.actions.readClaimProduct(pin, true);

        }

    } else {

        this::resetCustomerProductDetails();

    }

}

function handleResponsibleAccountChange(accountId) {

    this.actions.updateResponsibleAccountsDropDown(accountId, this.selectedAccounts, this.claim.pin);
    this.actions.readSpaTypes(accountId);

}

function handleUseAmountChange(amount) {

    this.actions.updateSpaClaimUseAmount(amount);

}

function handleUnitOptionChange(key) {

    this.actions.updateSpaClaimUnitDropDown(key, this.product.useAmountAllowedUnits);

}

function handleWorkOrderOneChange(workOrder) {

    this.actions.updateSpaClaimWorkOrderOne(workOrder);

}

function handleFailurePartChange(partNumber) {

    this.actions.updateSpaClaimFailurePart(partNumber);

}

function handleFailureDateChange(date) {

    this.actions.updateSpaClaimFailureDate(date);

}

function handleRepairDateChange(date) {

    this.actions.updateSpaClaimRepairDate(date);

}

function handleButtonClick() {

    const action = new ClaimAction({
        actionName: 'VALIDATE',
        id: 'VALIDATE',
        mode: ''
    });

    this.actions.updateSelectedAction(action);

}

function handleOwnershipStatusChange(event) {

    this.actions.updateOwnershipStatusDropDown(event.target.value, this.ownershipStatus);

}

function handleDropDownBlur(property, event) {

    this.actions.updateSpaClaimInputOnBlur(property, event.target.value, this.claim);

}

function handleWorkOrderTwoChange(workOrder) {

    this.actions.updateSpaClaimWorkOrderTwo(workOrder);

}

function handleRepairNumberChange(repairNumber) {

    this.actions.updateSpaClaimRepairNumber(repairNumber);

}

function handleSpaTypeChange(event) {

    this.actions.updateSpaClaimType(event.target.value, this.spaTypes);
    this.actions.readOtherCreditDescriptions('SPA', event.target.value);

    if (event.target.value === '12' || this.claim.specialAllowanceType.key === '12' && event.target.value !== '12') {

        this.actions.resetSpaFieldsForUptimeClaim();

    }

}

function handleClaimPropertyChange(property, value) {

    this.actions.updateSpaClaimProperty(property, value);

}

function handlePinChange(pin, event) {

    const formattedPin = formatPin(pin);

    this.actions.updateSpaClaimPin(formattedPin);
    fixCursorPosition(event, formattedPin);

}

function handleDateBlur(property, date) {

    const modifiedDate = isValidDate(date.target.value) ? moment.utc(date.target.value).valueOf() : null;

    this.actions.updateSpaClaimInputOnBlur(property, modifiedDate, this.claim);

}

const canEditPin = claim =>
    claim.claimStatus.key === 'ZDFT' || !claim.claimStatus.key;

const ClaimDetailsContainer = props => {

    const canEditResponsibleAccount = isResponsibleAccountEditable(props.isEditable, props.claim.claimStatus);

    return (
        <section className='claim-details-section'>
            <div className='claim-details-section-header'>
                <h3>
                    <FormattedMessage id='PRODUCT_INFORMATION'/>
                </h3>
                {props.claim.rgaStatus.status && props.claim.rgaStatus.status !== 'NONE' &&
                    <ClaimRgaStatusIndicator
                        claim={props.claim.claimNumber}
                        claimRgaStatus={props.claim.rgaStatus}
                    />
                }
            </div>
            <div className='product-info'>
                <ResponsibleAccounts
                    dealers={props.selectedAccounts}
                    errorMessage='DEALER_LOCATION_IS_REQUIRED'
                    isEditable={canEditResponsibleAccount}
                    isValid={props.formValidation.responsibleAccount}
                    onOptionBlur={handleInputBlur.bind(props, 'responsibleAccount')}
                    onOptionChange={props::handleResponsibleAccountChange}
                    selectedAccount={props.claim.responsibleAccount}
                />
                <LabeledKeyValueDropDown
                    className='spa-type'
                    errorMessage='SPECIAL_ALLOWANCE_TYPE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'spaType', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.spaType}
                    label='SPECIAL_ALLOWANCE_TYPE'
                    list={props.spaTypes}
                    onBlur={handleDropDownBlur.bind(props, 'spaType')}
                    onChange={props::handleSpaTypeChange}
                    selectedValue={props.claim.specialAllowanceType}
                    vsrMessage={getVsrMessage(props.claim.errors, 'spaType', props.isEditable)}
                />
            </div>
            <div className='spa-info'>
                <LabeledInputWithBlur
                    className={`pin ${props.claim.model && props.claim.serialNumber ? '' : 'claim-required'}`}
                    errorMessage='PIN_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'pin', props.isEditable)}
                    isEditable={props.isEditable && canEditPin(props.claim)}
                    isValid={props.formValidation.pin}
                    label='PIN'
                    maxLength={18}
                    onBlur={props::handlePinBlur}
                    onChange={props::handlePinChange}
                    value={props.claim.pin}
                    vsrMessage={getVsrMessage(props.claim.errors, 'pin', props.isEditable)}
                />
                {props.claim.specialAllowanceType.key === SERVICE_PART_FAILURE &&
                    <div className='replacement-info'>
                        <LabeledInput
                            className={`model ${props.claim.pin ? '' : 'claim-required'}`}
                            highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'model', props.isEditable)}
                            isEditable={props.isEditable}
                            label='MODEL'
                            maxLength={5}
                            onChange={handleClaimPropertyChange.bind(props, 'model')}
                            value={props.claim.model}
                            vsrMessage={getVsrMessage(props.claim.errors, 'model', props.isEditable)}
                        />
                        <LabeledInput
                            className={`serial-number ${props.claim.pin ? '' : 'claim-required'}`}
                            highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'serialNumber', props.isEditable)}
                            isEditable={props.isEditable}
                            label='SERIAL_NUMBER'
                            maxLength={6}
                            onChange={handleClaimPropertyChange.bind(props, 'serialNumber')}
                            value={props.claim.serialNumber}
                            vsrMessage={getVsrMessage(props.claim.errors, 'serialNumber', props.isEditable)}
                        />
                    </div>
                }
            </div>
            <h3>
                <FormattedMessage id='REFERENCE_INFORMATION'/>
            </h3>
            <div className='reference-info'>
                <LabeledInputWithBlur
                    className='work-order-one'
                    errorMessage='WORK_ORDER_ONE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderOne', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.workOrderOne}
                    label='WORK_ORDER_ONE'
                    maxLength={26}
                    onBlur={handleInputBlur.bind(props, 'workOrderOne')}
                    onChange={props::handleWorkOrderOneChange}
                    value={props.claim.referenceInfo.referenceNumber1}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderOne', props.isEditable)}
                />
                <LabeledInput
                    className='work-order-two'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderTwo', props.isEditable)}
                    isEditable={props.isEditable}
                    label='WORK_ORDER_TWO'
                    maxLength={12}
                    onChange={props::handleWorkOrderTwoChange}
                    value={props.claim.referenceInfo.referenceNumber2}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderTwo', props.isEditable)}
                />
                <LabeledInput
                    className='repair-number'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairNumber', props.isEditable)}
                    isEditable={props.isEditable}
                    label='REPAIR_NUMBER'
                    maxLength={13}
                    onChange={props::handleRepairNumberChange}
                    value={props.claim.referenceInfo.repairNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairNumber', props.isEditable)}
                />
                <LabeledKeyValueDropDown
                    className='ownership-status'
                    errorMessage='OWNERSHIP_STATUS_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'ownershipStatus', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.ownershipStatus}
                    label='OWNERSHIP_STATUS'
                    list={props.ownershipStatus}
                    onBlur={handleDropDownBlur.bind(props, 'ownershipStatus')}
                    onChange={props::handleOwnershipStatusChange}
                    selectedValue={props.claim.referenceInfo.ownershipStatus}
                    vsrMessage={getVsrMessage(props.claim.errors, 'ownershipStatus', props.isEditable)}
                />
            </div>
            {props.claim.specialAllowanceType.key === SERVICE_PART_FAILURE &&
                <div className='replacement-fields'>
                    <LabeledInputWithBlur
                        className='previous-invoice-number'
                        errorMessage='PREVIOUS_INVOICE_NUMBER_IS_REQUIRED'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'previousInvoiceNumber', props.isEditable)}
                        isEditable={props.isEditable}
                        isValid={props.formValidation.previousInvoiceNumber}
                        label='PREVIOUS_INVOICE_NUMBER'
                        maxLength={15}
                        onBlur={handleInputBlur.bind(props, 'previousInvoiceNumber')}
                        onChange={handleClaimPropertyChange.bind(props, 'previousInvoiceNumber')}
                        value={props.claim.previousInvoiceNumber}
                        vsrMessage={getVsrMessage(props.claim.errors, 'previousInvoiceNumber', props.isEditable)}
                    />
                    <LabeledDateInput
                        className='retail-install-date'
                        date={props.claim.retailInstallDate}
                        errorMessage='RETAIL_INSTALL_IS_REQUIRED'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'retailInstallDate', props.isEditable)}
                        isEditable={props.isEditable}
                        isValid={props.formValidation.retailInstallDate}
                        label='RETAIL_INSTALL'
                        maxDate={moment.utc(props.claim.failureInfo.failureDate)}
                        onBlur={handleDateBlur.bind(props, 'retailInstallDate')}
                        onDateChange={handleClaimPropertyChange.bind(props, 'retailInstallDate')}
                        vsrMessage={getVsrMessage(props.claim.errors, 'retailInstallDate', props.isEditable)}
                    />
                </div>
            }
            <div className='failure-info'>
                <LabeledDateInput
                    className='failure-date'
                    date={props.claim.failureInfo.failureDate}
                    errorMessage='FAILED_DATE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failureDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.failureDate}
                    label='FAILED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'failureDate')}
                    onDateChange={props::handleFailureDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failureDate', props.isEditable)}
                />
                <LabeledDateInput
                    className={getFieldClassNameForClaimFied('repair-date', props.claim.specialAllowanceType, 'claim-required')}
                    date={props.claim.failureInfo.repairDate}
                    errorMessage={getRepairedDateErrorMessage(props.claim.failureInfo.repairDate)}
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={isValidationRequiredForClaimFied(props.claim.specialAllowanceType, props.formValidation.repairDate) && isRepairDateAfterFailureDate(props.claim.failureInfo.repairDate, props.claim.failureInfo.failureDate)}
                    label='REPAIRED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'repairDate')}
                    onDateChange={props::handleRepairDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairDate', props.isEditable)}
                />
                {props.product.useAmountAllowedUnits.length > 0 &&
                    <AmountOfUse
                        allowedUnits={props.product.useAmountAllowedUnits}
                        className='amount-of-use'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'amountOfUse', props.isEditable)}
                        isEditable={props.isEditable}
                        isValid={props.formValidation.useAmount && props.formValidation.useAmountUnit}
                        onInputBlur={handleInputBlur.bind(props, 'useAmount')}
                        onInputChange={props::handleUseAmountChange}
                        onOptionBlur={handleInputBlur.bind(props, 'useAmountUnit')}
                        onOptionChange={props::handleUnitOptionChange}
                        selectedUnit={props.claim.useAmount.unit}
                        showBlankOption={false}
                        value={props.claim.useAmount.amount}
                        vsrMessage={getVsrMessage(props.claim.errors, 'amountOfUse', props.isEditable)}
                    />
                }
                <LabeledInputWithBlur
                    className={getFieldClassNameForClaimFied('failure-part', props.claim.specialAllowanceType, 'claim-required')}
                    errorMessage='FAILED_PART_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failurePart', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={isValidationRequiredForClaimFied(props.claim.specialAllowanceType, props.formValidation.failurePart)}
                    label='FAILED_PART'
                    maxLength={18}
                    onBlur={handleInputBlur.bind(props, 'failurePart')}
                    onChange={props::handleFailurePartChange}
                    value={props.claim.failureInfo.failurePartNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failurePart', props.isEditable)}
                />
            </div>
            {(props.isEditable && props.claim.showValidate) &&
                <div className='button-group'>
                    <button
                        disabled={!isValidClaim(props.claim, props.product.useAmountAllowedUnits.length)}
                        onClick={props::handleButtonClick}
                    >
                        <FormattedMessage id='VALIDATE'/>
                    </button>
                </div>
            }
        </section>
    );

};

ClaimDetailsContainer.displayName = 'ClaimDetailsContainer';
ClaimDetailsContainer.propTypes = {
    actions: PropTypes.object.isRequired,
    claim: PropTypes.instanceOf(SpaClaim).isRequired,
    formValidation: PropTypes.instanceOf(FormValidation).isRequired,
    isEditable: PropTypes.bool.isRequired,
    isValidClaim: PropTypes.bool.isRequired,
    ownershipStatus: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    product: PropTypes.instanceOf(Product).isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired,
    spaTypes: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired
};

export default ClaimDetailsContainer;



// WEBPACK FOOTER //
// ./src/claim/spa/components/claim-details-section/claim-details-container.js